// SHADOW NETWORK — Analysis comments section (additive).
// Inline comments panel that expands below an AnalysisCard. Any authenticated
// user (FREE or PRO) can comment. Guests see a CTA to login. Admin comments
// are highlighted with a cyan border + "◈ SHADOW ADMIN" badge. Owners and
// admins can delete individual comments.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";
import { MentionTextarea } from "./shadow/MentionTextarea";
import { renderWithMentions } from "./shadow/renderWithMentions";

const timeAgo = (iso, ui) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  const pref = ui.acm_ago_prefix ? `${ui.acm_ago_prefix} ` : "";
  if (diff < 60) return ui.acm_ago_now;
  if (diff < 3600) return `${pref}${Math.floor(diff / 60)}m`.trim();
  if (diff < 86400) return `${pref}${Math.floor(diff / 3600)}h`.trim();
  if (diff < 604800) return `${pref}${Math.floor(diff / 86400)}d`.trim();
  return new Date(iso).toLocaleDateString();
};

// Deterministic avatar color from user id (so the same user always gets the
// same chip across renders).
const avatarColor = (id = "") => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  const palette = ["#f97316", "#fbbf24", "#22d3ee", "#a855f7", "#10b981", "#ef4444", "#3b82f6", "#ec4899"];
  return palette[Math.abs(h) % palette.length];
};

export const AnalysisComments = ({ postId, onCountChange }) => {
  const { user, openLogin, openRegister } = useAuth();
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/shadow-posts/${postId}/comments`, {
        credentials: "include",
      });
      const data = await safeJson(res);
      if (res.ok && data) {
        setComments(data.comments || []);
        onCountChange?.((data.comments || []).length);
      }
    } finally {
      setLoading(false);
    }
  }, [postId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || sending) return;
    if (t.length > 280) { setError(ui.acm_max_chars); return; }
    setSending(true);
    setError(null);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-posts/${postId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ text: t }),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        const msg = data?.detail?.message || data?.detail || data?.error || ui.acm_error_generic;
        throw new Error(typeof msg === "string" ? msg : ui.acm_error_generic);
      }
      const next = [...comments, data.comment];
      setComments(next);
      onCountChange?.(next.length);
      setText("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm("¿Eliminar este comentario?")) return;
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-posts/${postId}/comments/${commentId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (res.ok) {
        const next = comments.filter((c) => c.id !== commentId);
        setComments(next);
        onCountChange?.(next.length);
      }
    } catch {}
  };

  return (
    <div className="border-t border-white/[0.06] bg-black/30 px-4 py-3" data-testid={`comments-${postId}`}>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
        {ui.acm_header} ({comments.length})
      </p>

      {loading && comments.length === 0 ? (
        <div className="flex items-center gap-2 py-3 text-[11px] text-white/40">
          <Loader2 className="h-3 w-3 animate-spin" /> {ui.acm_loading}
        </div>
      ) : comments.length === 0 ? (
        <p className="py-2 text-[11px] text-white/30">{ui.acm_empty}</p>
      ) : (
        <ul className="divide-y divide-white/[0.04]">
          <AnimatePresence initial={false}>
            {comments.map((c) => {
              const isAdmin = !!c.user_is_admin;
              const canDelete = user && (c.user_id === user.id || user.is_admin);
              return (
                <motion.li
                  key={c.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className={`comment-row ${isAdmin ? "comment-admin" : ""}`}
                  data-testid={`comment-${c.id}`}
                >
                  {c.user_username ? (
                    <Link
                      to={`/shadow/perfil/${c.user_username}`}
                      onClick={(e) => e.stopPropagation()}
                      className="comment-avatar shrink-0 transition-all hover:ring-2 hover:ring-amber-400/60"
                      style={!isAdmin ? { background: avatarColor(c.user_id || c.id) } : undefined}
                      aria-label={`Ver perfil de ${c.user_name || c.user_username}`}
                      data-testid={`comment-author-avatar-${c.id}`}
                    >
                      {(c.user_name || "?").trim().charAt(0).toUpperCase()}
                    </Link>
                  ) : (
                    <div
                      className="comment-avatar"
                      style={!isAdmin ? { background: avatarColor(c.user_id || c.id) } : undefined}
                      aria-hidden
                    >
                      {(c.user_name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {c.user_username ? (
                        <Link
                          to={`/shadow/perfil/${c.user_username}`}
                          onClick={(e) => e.stopPropagation()}
                          className={`comment-author-name text-xs font-bold transition-colors hover:text-amber-300 ${isAdmin ? "" : "text-white/85"}`}
                          data-testid={`comment-author-name-${c.id}`}
                        >
                          {c.user_name}
                        </Link>
                      ) : (
                        <span className={`comment-author-name text-xs font-bold ${isAdmin ? "" : "text-white/85"}`}>
                          {c.user_name}
                        </span>
                      )}
                      {isAdmin && (
                        <span className="badge-shadow-admin" data-testid={`comment-admin-badge-${c.id}`}>
                          <ShieldCheck className="h-2.5 w-2.5" /> {ui.acm_admin_badge}
                        </span>
                      )}
                      <span className="text-[10px] text-white/35">· {timeAgo(c.created_at, ui)}</span>
                    </div>
                    <p className="mt-0.5 break-words text-[12.5px] leading-relaxed text-white/75">{renderWithMentions(c.text)}</p>
                  </div>
                  {canDelete && (
                    <button
                      onClick={() => remove(c.id)}
                      className="ml-1 rounded p-1 text-white/30 transition-colors hover:bg-rose-500/10 hover:text-rose-300"
                      aria-label={ui.acm_delete_aria}
                      data-testid={`comment-delete-${c.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}

      {/* Composer */}
      {user ? (
        <form onSubmit={send} className="mt-3 flex items-start gap-2">
          <MentionTextarea
            value={text}
            onChange={(val) => setText(val.slice(0, 280))}
            placeholder={ui.acm_placeholder}
            rows={1}
            maxLength={280}
            className="min-h-[36px] flex-1 resize-none rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs outline-none focus:border-cyan-400/50"
            dataTestid={`comment-input-${postId}`}
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="inline-flex h-[36px] items-center gap-1 rounded-lg border border-cyan-400/40 bg-cyan-400/[0.06] px-3 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-200 transition-colors hover:bg-cyan-400/[0.15] disabled:opacity-50"
            data-testid={`comment-submit-${postId}`}
          >
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            {ui.acm_submit}
          </button>
        </form>
      ) : (
        <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-[11px] text-white/55">
          <button
            onClick={openLogin}
            className="font-bold text-cyan-300 hover:underline"
            data-testid={`comment-login-${postId}`}
          >
            {ui.acm_login}
          </button>
          {ui.acm_login_suffix}
          <button
            onClick={openRegister}
            className="font-bold text-orange-300 hover:underline"
            data-testid={`comment-register-${postId}`}
          >
            {ui.acm_register}
          </button>
        </div>
      )}

      {error && <p className="mt-2 text-[11px] text-rose-300" data-testid={`comment-error-${postId}`}>{error}</p>}

      <div className="mt-1.5 text-right text-[9px] uppercase tracking-widest text-white/25">
        {text.length}/280
      </div>
    </div>
  );
};

export default AnalysisComments;
